/**
 * ErrorMessage Component
 * 
 * Displays error messages with optional retry functionality.
 * Used for displaying persistent errors that require user action.
 * 
 * Requirements: 7.5, 9.3
 */

export interface ErrorMessageProps {
  /** Error message to display */
  message: string;
  /** Optional title for the error */
  title?: string;
  /** Optional retry callback */
  onRetry?: () => void;
  /** Optional cancel/dismiss callback */
  onCancel?: () => void;
  /** Retry button label */
  retryLabel?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Whether to show as full-screen */
  fullScreen?: boolean;
}

/**
 * ErrorMessage component - Displays error with action buttons
 */
export function ErrorMessage({
  message,
  title = 'Error',
  onRetry,
  onCancel,
  retryLabel = 'Retry',
  cancelLabel = 'Cancel',
  fullScreen = false,
}: ErrorMessageProps) {
  const content = (
    <div className="text-center max-w-md mx-auto">
      <div className="text-6xl mb-4" data-testid="error-icon">
        ❌
      </div>
      <h2
        className="text-3xl font-bold mb-4 text-red-600"
        data-testid="error-title"
      >
        {title}
      </h2>
      <p
        className="text-lg mb-8 text-gray-700"
        data-testid="error-message"
      >
        {message}
      </p>
      <div className="flex gap-4 justify-center">
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-8 py-4 rounded-2xl bg-yellow-500 text-white font-bold text-lg shadow-lg hover:bg-yellow-600 hover:shadow-xl transition-all"
            data-testid="error-retry-button"
          >
            {retryLabel}
          </button>
        )}
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-8 py-4 rounded-2xl bg-gray-200 text-gray-700 font-bold text-lg shadow-lg hover:bg-gray-300 hover:shadow-xl transition-all"
            data-testid="error-cancel-button"
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className="flex items-center justify-center min-h-[600px] bg-white rounded-3xl shadow-xl p-12"
        data-testid="error-message-fullscreen"
      >
        {content}
      </div>
    );
  }

  return (
    <div
      className="bg-red-50 border border-red-200 rounded-lg p-6"
      data-testid="error-message"
    >
      {content}
    </div>
  );
}
